<template>
  <div class="c-tabs">
    <ul>
      <!-- D: 활성화시 is-active 및 aria-selected 추가 -->
      <li class="tab-item">
        <a
          href="#"
          class="tab-anchor"
          :class="{ 'is-active': selectedTopCategoryCd === CATEGORY_CDS[0] }"
          @click.prevent="() => selectTopCategory(CATEGORY_CDS[0])"
          role="tab"
          aria-selected
        >
          건축(<span class="system"
            >{{ Utils.currency(getTopCategoryCount(CATEGORY_CDS[0])) }} </span
          >)
        </a>
      </li>
      <li class="tab-item">
        <a
          href="#tab3-2"
          class="tab-anchor"
          :class="{ 'is-active': selectedTopCategoryCd === CATEGORY_CDS[1] }"
          @click.prevent="() => selectTopCategory(CATEGORY_CDS[1])"
          role="tab"
        >
          공업(<span class="system"
            >{{ Utils.currency(getTopCategoryCount(CATEGORY_CDS[1])) }} </span
          >)
        </a>
      </li>
      <li class="tab-item">
        <a
          href="#tab3-3"
          class="tab-anchor"
          :class="{ 'is-active': selectedTopCategoryCd === CATEGORY_CDS[2] }"
          @click.prevent="() => selectTopCategory(CATEGORY_CDS[2])"
          role="tab"
        >
          자보(<span class="system"
            >{{ Utils.currency(getTopCategoryCount(CATEGORY_CDS[2])) }} </span
          >)
        </a>
      </li>
    </ul>
  </div>
  <!-- D: c-tab-panel과 c-tab-panel-wrap은 세트임 -->
  <div class="c-tab-panel-wrap" v-if="showCategoryOptionsYn">
    <!-- tab content: tab1 -->
    <div class="tab-panel" role="tabpanel" aria-labelledby="tab3-1">
      <!-- D: PC에만 노출 -->
      <div class="c-use-table only-pc">
        <table>
          <caption>
            제품군 선택 테이블
          </caption>
          <colgroup>
            <col style="width: 17.937%" />
            <col />
          </colgroup>
          <tbody>
            <SearchCategoryRow
              v-if="lvl2Categories"
              :category-name="'시장'"
              v-model:categories="lvl2Categories"
              @select-changed="() => emits('selectChanged')"
            />
            <SearchCategoryRow
              v-if="lvl3Categories"
              :category-name="'브랜드'"
              :category-flag="vSize"
              v-model:categories="lvl3Categories"
              @select-changed="() => emits('selectChanged')"
            />
            <SearchCategoryRow
              v-if="lvl4Categories"
              :category-name="'제품군'"
              v-model:categories="lvl4Categories"
              @select-changed="() => emits('selectChanged')"
            />
          </tbody>
        </table>
      </div>
      <!-- // D: PC에만 노출 -->

      <!-- D: MO에만 노출 -->
      <div class="mo-filter-wrap only-mo">
        <p class="filter-info-txt">
          아이콘을 눌러 제품 용도별 카테고리를 확인해보세요 ▶
        </p>
        <div class="filter-items">
          <button
            v-for="item in mobileSelectedCategories"
            :key="item.clsfCd"
            type="button"
            class="filter-item"
          >
            <span
              >{{ item.clsfNm }}({{ Utils.currency(item.productCount) }})</span
            >
          </button>
        </div>
        <button
          type="button"
          class="btn-filter"
          @click="() => (isShowMobileFilterPop = true)"
        >
          <span class="sr-only">필터설정</span>
        </button>
      </div>
      <!-- //  D: MO에만 노출 -->
    </div>
  </div>

  <SearchCategorySelectedItems
    :selected-categories="selectedCategories"
    @unset-all="unsetAll"
    @select-changed="() => emits('selectChanged')"
  />

  <MobileFilterPop
    v-if="isShowMobileFilterPop && searchCategories"
    :searchCategories="searchCategories"
    :searchParams="props.searchParams"
    v-model:selectedTopCategoryCd="selectedTopCategoryCd"
    @close="() => (isShowMobileFilterPop = false)"
    @categories-selected="onMobileCategoriesSelected"
  />
</template>
<script setup>
import {
  onMounted,
  defineModel,
  ref,
  computed,
  defineEmits,
  defineProps,
  nextTick,
  watch,
} from "vue";
import SearchCategoryRow from "@/components/paintpro/prod/searchCategoryRow.vue";
import SearchCategorySelectedItems from "@/components/paintpro/prod/searchCategorySelectedItems.vue";
import MobileFilterPop from "@/components/paintpro/prod/moProdFilterPop.vue";
import Utils from "@/js/utils";

const emits = defineEmits(["selectChanged"]);

const props = defineProps({
  salesBranch: {
    type: Object,
    required: true,
  },
  searchParams: {
    type: Object,
    required: true,
  }
});

const CATEGORY_CDS = ["P0101", "P0102", "P0103"]; // P0101: 건축, P0102: 공업용, P0103: 자보용
const searchCategories = defineModel("searchCategories", { required: true });
const selectedTopCategoryCd = ref(CATEGORY_CDS[0]);
const isShowMobileFilterPop = ref(false);
const showCategoryOptionsYn = ref(true);

onMounted(() => {
  selectedTopCategoryCd.value = props.salesBranch.matkl;
  reRenderShowCategoryOptionsYn();
  checkSelectedTopCategory();
});

const salesBranch = computed(() => {
  return props.salesBranch;
});

watch(searchCategories, () => {
  checkSelectedTopCategory();
  reRenderShowCategoryOptionsYn();
});

watch(salesBranch, () => {
  selectedTopCategoryCd.value = salesBranch.value.matkl;
  checkSelectedTopCategory();
  reRenderShowCategoryOptionsYn();
});

const lvl2Categories = computed(() => {
  if (!searchCategories.value) return [];

  return searchCategories.value.lvl2Categories.filter(
    (item) => item.topCd === selectedTopCategoryCd.value
  );
});

const lvl3Categories = computed(() => {
  if (!searchCategories.value) return [];

  return searchCategories.value.lvl3Categories.filter(
    (item) => item.topCd === selectedTopCategoryCd.value
  );
});

const lvl4Categories = computed(() => {
  if (!searchCategories.value) return [];

  return searchCategories.value.lvl4Categories.filter(
    (item) => item.topCd === selectedTopCategoryCd.value
  );
});

const selectedCategories = computed(() => {
  if (!searchCategories.value) return [];

  return [
    ...searchCategories.value.lvl2Categories,
    ...searchCategories.value.lvl3Categories,
    ...searchCategories.value.lvl4Categories,
  ].filter((item) => item.selected === true);
});

const mobileSelectedCategories = computed(() => {
  if (!searchCategories.value) return [];

  return [
    ...searchCategories.value.lvl2Categories,
    ...searchCategories.value.lvl3Categories,
    ...searchCategories.value.lvl4Categories,
  ]
    .filter((item) => item.selected === true)
    .filter((item) => item.topCd === selectedTopCategoryCd.value);
});

/**
 * 선택된 TOP 카테고리의 개수를 파악하여 0인 경우 카테고리가 존재하는 TOP 카테고리로 자동 변환한다.
 *
 */
const checkSelectedTopCategory = () => {
  if (getTopCategoryCount(selectedTopCategoryCd.value) === 0) {
    for (let categoryCd of CATEGORY_CDS) {
      if (getTopCategoryCount(categoryCd) > 0) {
        selectedTopCategoryCd.value = categoryCd;
        break;
      }
    }
  }
};

const unsetAll = () => {
  [
    ...searchCategories.value.lvl2Categories,
    ...searchCategories.value.lvl3Categories,
    ...searchCategories.value.lvl4Categories,
  ].forEach((item) => (item.selected = false));

  emits("selectChanged");
};

const getTopCategoryCount = (code) => {
  if (!searchCategories.value) return 0;

  const category = searchCategories.value.topCategories.find(
    (item) => item.clsfCd === code
  );

  return category?.productCount || 0;
};

const onMobileCategoriesSelected = (newCategories) => {
  searchCategories.value = newCategories;
  isShowMobileFilterPop.value = false;

  emits("selectChanged");
};

const selectTopCategory = (code) => {
  if (getTopCategoryCount(code) === 0) return;

  selectedTopCategoryCd.value = code;

  reRenderShowCategoryOptionsYn();
};

const reRenderShowCategoryOptionsYn = () => {
  showCategoryOptionsYn.value = false;
  nextTick(() => {
    showCategoryOptionsYn.value = true;
  });
};
</script>
